/**
 * AdminResetPasswordModal — fired from the Members page when an admin
 * clicks "Reset password" for a target user. Lets the admin choose
 * between three delivery paths (the four-path system from the kickoff
 * doc minus the CLI bootstrap, which is shell-only):
 *
 *   1. Generate Reset Link — returns a URL, shown ONCE for the admin
 *      to copy + share OOB (Signal, Slack DM, whatever).
 *   2. Generate Temporary Password — returns a 4-word + 4-digit temp
 *      password, shown ONCE. User must change on next sign-in.
 *   3. Send Reset Email — SMTP-only. Emails the same kind of URL as #1
 *      directly to the target user.
 *
 * Smart visibility: when SMTP isn't configured, the email option is
 * shown disabled with a hint that admin-link covers the same role.
 */

import { useEffect, useState } from 'react';
import { ModalCoverBand } from './ModalCoverBand';
import { useDebugRegister, debugLog } from '../lib/debug';

type Method = 'admin_link' | 'admin_temp_password' | 'admin_email';

interface Props {
  targetUser: {
    id: string;
    email: string;
    display_name: string;
  };
  smtpEnabled: boolean;
  onClose: () => void;
}

interface ResetLinkResult {
  url: string;
  expires_at: string;
}
interface TempPasswordResult {
  temporary_password: string;
  expires_at: string;
  /** True when issuing the temp password also flipped a `pending` user
   *  to `active`. UI surfaces this so the admin knows what happened. */
  activated_pending_user?: boolean;
}

export function AdminResetPasswordModal({ targetUser, smtpEnabled, onClose }: Props) {
  const [method, setMethod] = useState<Method>('admin_link');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [linkResult, setLinkResult] = useState<ResetLinkResult | null>(null);
  const [tempResult, setTempResult] = useState<TempPasswordResult | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  useDebugRegister('AdminResetPasswordModal', {
    target_user_id: targetUser.id,
    target_email: targetUser.email,
    method,
    busy,
    smtp_enabled: smtpEnabled,
    has_link_result: !!linkResult,
    has_temp_result: !!tempResult,
    email_sent: emailSent,
    error: err,
  });

  useEffect(() => {
    debugLog.recordEvent({
      type: 'admin_reset_modal_opened',
      description: `admin opened reset modal for ${targetUser.email}`,
      data: { target_user_id: targetUser.id, smtp_enabled: smtpEnabled },
    });
  }, [targetUser.id, targetUser.email, smtpEnabled]);

  async function handleGenerate() {
    setBusy(true);
    setErr(null);
    setLinkResult(null);
    setTempResult(null);
    setEmailSent(false);
    const path =
      method === 'admin_link'
        ? '/api/v1/auth/local/admin-reset-link'
        : method === 'admin_temp_password'
          ? '/api/v1/auth/local/admin-temp-password'
          : '/api/v1/auth/local/admin-send-reset';
    debugLog.recordEvent({
      type: 'admin_reset_generate_clicked',
      description: `admin chose ${method} for ${targetUser.email}`,
      data: { target_user_id: targetUser.id, method, path },
    });
    try {
      const startedAt = performance.now();
      const r = await fetch(path, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: targetUser.id }),
      });
      const durationMs = performance.now() - startedAt;
      debugLog.recordApiCall({
        channel: path,
        path,
        payload: { user_id: targetUser.id },
        ok: r.ok,
        status: r.status,
        durationMs,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => null);
        debugLog.recordEvent({
          type: 'admin_reset_failed',
          description: `${j?.code ?? `HTTP_${r.status}`}: ${j?.message ?? 'Failed'}`,
          data: { target_user_id: targetUser.id, method, code: j?.code, status: r.status },
        });
        setErr(`${j?.code ?? `HTTP_${r.status}`}: ${j?.message ?? 'Failed.'}`);
        return;
      }
      const data = await r.json();
      debugLog.recordEvent({
        type: 'admin_reset_succeeded',
        description: `${method} delivery completed for ${targetUser.email}`,
        data: { target_user_id: targetUser.id, method },
      });
      if (method === 'admin_link') setLinkResult({ url: data.url, expires_at: data.expires_at });
      else if (method === 'admin_temp_password')
        setTempResult({
          temporary_password: data.temporary_password,
          expires_at: data.expires_at,
          activated_pending_user: data.activated_pending_user === true,
        });
      else setEmailSent(true);
    } catch (err) {
      debugLog.recordEvent({
        type: 'admin_reset_network_error',
        description: 'fetch failed before response',
        data: { target_user_id: targetUser.id, method, error: err instanceof Error ? err.message : String(err) },
      });
      setErr('Could not reach the server. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older browsers — surface as a hint to manually select.
    }
  }

  const hasResult = !!(linkResult || tempResult || emailSent);

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ padding: 0, maxWidth: 540 }}
        data-debug-id="admin-reset-modal"
      >
        <ModalCoverBand
          title={`Reset password — ${targetUser.display_name}`}
          subtitle={targetUser.email}
          onClose={onClose}
        />

        <div style={{ padding: '20px 24px' }}>
          {!hasResult && (
            <>
              <p style={{ marginTop: 0, fontSize: 13, color: '#7a7d8a' }}>
                Choose how to deliver the reset to {targetUser.display_name.split(' ')[0]}:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <label className="form-radio" data-debug-id="method-admin-link">
                  <input
                    type="radio"
                    name="reset-method"
                    checked={method === 'admin_link'}
                    onChange={() => setMethod('admin_link')}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>Generate Reset Link</div>
                    <div style={{ fontSize: 12, color: '#7a7d8a' }}>
                      Returns a URL shown to you ONCE — copy and share it with
                      the user out-of-band (Signal, DM, etc.). Expires in 24h.
                    </div>
                  </div>
                </label>

                <label className="form-radio" data-debug-id="method-temp-password">
                  <input
                    type="radio"
                    name="reset-method"
                    checked={method === 'admin_temp_password'}
                    onChange={() => setMethod('admin_temp_password')}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>Generate Temporary Password</div>
                    <div style={{ fontSize: 12, color: '#7a7d8a' }}>
                      Returns a 4-word + 4-digit temp password shown ONCE. The
                      user signs in with it, then is forced to change. Expires
                      in 24h.
                    </div>
                  </div>
                </label>

                <label
                  className={`form-radio ${smtpEnabled ? '' : 'form-radio-disabled'}`}
                  data-debug-id="method-email"
                  title={smtpEnabled ? '' : 'SMTP is not configured on this instance.'}
                >
                  <input
                    type="radio"
                    name="reset-method"
                    checked={method === 'admin_email'}
                    onChange={() => setMethod('admin_email')}
                    disabled={!smtpEnabled}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      Send Reset Email {smtpEnabled ? '' : '(SMTP not configured)'}
                    </div>
                    <div style={{ fontSize: 12, color: '#7a7d8a' }}>
                      Emails the user a reset link directly. Same token mechanics
                      as the manual link, just delivered via SMTP.
                    </div>
                  </div>
                </label>
              </div>

              {err && (
                <div className="form-error" style={{ marginTop: 16 }} data-debug-id="reset-error">
                  {err}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                <button className="button-secondary" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button
                  className="button-primary"
                  onClick={handleGenerate}
                  disabled={busy}
                  data-debug-id="generate-reset"
                >
                  {busy ? 'Working…' : 'Generate'}
                </button>
              </div>
            </>
          )}

          {linkResult && (
            <div data-debug-id="reset-link-result">
              <p style={{ fontWeight: 600, marginBottom: 6 }}>
                Reset link generated. Copy and share it now — it won't be shown again.
              </p>
              <div
                style={{
                  background: '#f0ede8',
                  border: '1px solid #d0ccc4',
                  padding: 12,
                  fontFamily: 'Courier New, monospace',
                  fontSize: 12,
                  wordBreak: 'break-all',
                  marginBottom: 8,
                }}
              >
                {linkResult.url}
              </div>
              <div style={{ fontSize: 12, color: '#7a7d8a', marginBottom: 16 }}>
                Expires at {new Date(linkResult.expires_at).toLocaleString()}.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="button-secondary"
                  onClick={() => copyToClipboard(linkResult.url)}
                  data-debug-id="copy-reset-link"
                >
                  Copy link
                </button>
                <button className="button-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}

          {tempResult && (
            <div data-debug-id="temp-password-result">
              <p style={{ fontWeight: 600, marginBottom: 6 }}>
                Temporary password generated. Share it once — it won't be shown again.
              </p>
              {tempResult.activated_pending_user && (
                <p
                  data-debug-id="activated-pending-note"
                  style={{
                    fontSize: 12,
                    color: '#1e6635',
                    background: 'rgba(45, 122, 66, 0.08)',
                    borderLeft: '3px solid #2d7a42',
                    padding: '8px 10px',
                    margin: '0 0 12px',
                  }}
                >
                  Note: this user was previously <strong>pending</strong>. Issuing them
                  credentials has activated their account.
                </p>
              )}
              <div
                style={{
                  background: '#f0ede8',
                  border: '1px solid #d0ccc4',
                  padding: 16,
                  fontFamily: 'Courier New, monospace',
                  fontSize: 18,
                  letterSpacing: 1,
                  marginBottom: 8,
                  textAlign: 'center',
                }}
              >
                {tempResult.temporary_password}
              </div>
              <div style={{ fontSize: 12, color: '#7a7d8a', marginBottom: 16 }}>
                Expires at {new Date(tempResult.expires_at).toLocaleString()}.
                User will be forced to change it on first sign-in.
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="button-secondary"
                  onClick={() => copyToClipboard(tempResult.temporary_password)}
                  data-debug-id="copy-temp-password"
                >
                  Copy password
                </button>
                <button className="button-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}

          {emailSent && (
            <div data-debug-id="email-sent-result">
              <p style={{ fontWeight: 600, marginBottom: 6 }}>
                Reset email sent to {targetUser.email}.
              </p>
              <p style={{ fontSize: 13, color: '#7a7d8a' }}>
                The user will receive a link with a 24-hour expiry. They can
                also use the "Forgot password?" link on the sign-in page if
                they need another.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="button-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
